import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getPermissionsFromToken, getUserRoles } from '../utils/jwtDecoder';
import { getAdminRoles } from '../utils/roleUtils';

interface PermissionContextType {
  permissions: string[];
  roles: string[];
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isLoading: boolean;
  canReadGames: boolean;
  canDownloadRegistrations: boolean;
  canCreateGames: boolean;
  canDeleteGames: boolean;
  canAccessCoopAdmin: boolean;
  isAnyAdmin: boolean;
  adminRoles: string[];
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading: authLoading } = useAuth0();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadPermissions = async () => {
      if (isAuthenticated && !authLoading) {
        try {
          setIsLoading(true);
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              scope: 'openid profile email read:games download:registrations'
            },
          });

          const userPermissions = getPermissionsFromToken(token);
          const userRoles = getUserRoles(token);

          // Enhanced debug logging
          console.group('🔐 AUTH TOKEN PERMISSIONS DEBUG');
          console.log('📄 Raw JWT Token (first 50 chars):', token.substring(0, 50) + '...');
          console.log('🔑 Extracted Permissions:', userPermissions);
          console.log('👤 Extracted Roles:', userRoles);
          
          // Show permission flags
          const userAdminRoles = getAdminRoles(userRoles);
          const isUserAdmin = userAdminRoles.length > 0;
          const canRead = userPermissions.includes('read:games') || isUserAdmin;
          const canDownload = userPermissions.includes('download:registrations') || isUserAdmin;
          
          console.log('🎯 Permission Flags:');
          console.log('  ├── Can Read Games:', canRead);
          console.log('  ├── Can Download:', canDownload);
          console.log('  ├── Admin Roles:', userAdminRoles);
          console.log('  └── Is Any Admin:', isUserAdmin);
          console.groupEnd();

          setPermissions(userPermissions);
          setRoles(userRoles);
        } catch (error) {
          console.error('Error loading permissions:', error);
          setPermissions([]);
          setRoles([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setPermissions([]);
        setRoles([]);
      }
    };

    loadPermissions();
  }, [isAuthenticated, authLoading, getAccessTokenSilently]);

  const checkPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const checkAnyPermission = (permissionList: string[]): boolean => {
    return permissionList.some(permission => permissions.includes(permission));
  };

  const checkRole = (role: string): boolean => {
    return roles.includes(role);
  };

  const checkAnyRole = (roleList: string[]): boolean => {
    return roleList.some(role => roles.includes(role));
  };

  // Get admin roles using the utility function
  const adminRoles = getAdminRoles(roles);
  const isAnyAdmin = adminRoles.length > 0;

  // Computed permission flags based on API requirements
  const canReadGames = checkAnyPermission(['read:games']) || isAnyAdmin;
  const canDownloadRegistrations = checkAnyPermission(['download:registrations']) || isAnyAdmin;
  const canCreateGames = checkAnyPermission(['create:games']) || isAnyAdmin;
  const canDeleteGames = checkAnyPermission(['delete:games']) || isAnyAdmin;
  const canAccessCoopAdmin = isAnyAdmin;

  const value: PermissionContextType = {
    permissions,
    roles,
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasRole: checkRole,
    hasAnyRole: checkAnyRole,
    isLoading: isLoading || authLoading,
    canReadGames,
    canDownloadRegistrations,
    canCreateGames,
    canDeleteGames,
    canAccessCoopAdmin,
    isAnyAdmin,
    adminRoles,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
